import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  defaultTokens,
  Divider,
  Label,
  Switch,
  Tabs,
  type ColorTokens,
  type ThemeMode
} from '@knack/asterisk-react';
import { colord } from 'colord';
import cloneDeep from 'lodash/cloneDeep';

import { capitalize } from '@/utils/formatters';
import { useThemesMessagingContext } from '@/pages/themes/theme-editor/MessagingContext';
import { Section } from '@/pages/themes/theme-editor/Section';
import { isPreviewModeDark } from '@/pages/themes/theme-editor/utils';
import {
  DEFAULT_KNACK_APP_THEME_COLOR_PRESET,
  useThemesPageContext
} from '@/pages/themes/ThemesPageContext';

type BackgroundTab = {
  value: 'light' | 'dark';
  children: React.ReactNode;
};

function getNormalizedHexColor(color: string) {
  const newColor = colord(color).toHex();
  if (color.length >= 8) {
    return newColor.substring(0, newColor.length - 2);
  }
  return newColor;
}

export function ColorList({ mode }: { mode: 'light' | 'dark' }) {
  const { theme, setThemeField } = useThemesPageContext();
  const tokens = theme.colors.customTokens || defaultTokens;

  return Object.keys(tokens[mode]).map((category) => (
    <Fragment key={`${mode}-${category}`}>
      <Divider className="my-5" />
      <Section sectionNameKey={`${category}_colors`}>
        {Object.keys(tokens[mode][category]).map((key) => (
          <div key={`${mode}-${category}-${key}`} className="flex items-center justify-between">
            <div>{capitalize(key.replaceAll('-', ' '))}</div>
            <input
              type="color"
              value={getNormalizedHexColor(tokens[mode][category][key])}
              onChange={(e) => {
                const newColors: ColorTokens = cloneDeep(tokens);
                newColors[mode][category][key] = getNormalizedHexColor(e.target.value);
                setThemeField((draft) => {
                  draft.colors.customTokens = newColors;
                });
              }}
            />
          </div>
        ))}
      </Section>
    </Fragment>
  ));
}

export function Advanced() {
  const [t] = useTranslation();
  const { sendMessageToLiveApp } = useThemesMessagingContext();
  const { theme, setThemeField } = useThemesPageContext();
  const [activeBackgroundTabValue, setActiveBackgroundTabValue] = useState<BackgroundTab['value']>(
    isPreviewModeDark(theme.mode) ? 'dark' : 'light'
  );
  const backgroundTabs = [
    {
      children: <span>{t('themes.light_mode')}</span>,
      value: 'light'
    },
    {
      children: <span>{t('themes.dark_mode')}</span>,
      value: 'dark'
    }
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="mt-2 flex items-center gap-2">
        <Switch
          id="use-custom-colors"
          checked={theme.colors.preset === 'custom'}
          onCheckedChange={(state) => {
            setThemeField((draft) => {
              draft.colors.preset = state ? 'custom' : DEFAULT_KNACK_APP_THEME_COLOR_PRESET;
            });
          }}
        />
        <Label htmlFor="use-custom-colors">{t('themes.use_custom_colors')}</Label>
      </div>
      {theme.colors.preset === 'custom' && (
        <>
          <Tabs
            defaultValue={activeBackgroundTabValue}
            onValueChange={(value) => {
              setActiveBackgroundTabValue(value as 'light' | 'dark');
              sendMessageToLiveApp({
                action: 'set-theme-preview-mode',
                mode: value as ThemeMode
              });
            }}
          >
            <Tabs.List items={backgroundTabs} shouldDisableResponsive />
            <Tabs.Content value="light">
              <ColorList mode="light" />
            </Tabs.Content>
            <Tabs.Content value="dark">
              <ColorList mode="dark" />
            </Tabs.Content>
          </Tabs>
          <Button
            intent="destructive"
            onClick={() => {
              setThemeField((draft) => {
                draft.colors.customTokens = null;
              });
            }}
          >
            {t('themes.reset_colors')}
          </Button>
        </>
      )}
    </div>
  );
}
